import React, { useContext } from "react";

import AnswersContext from "../contexts/AnswersContext";
import useQuestion from "../hooks/useQuestion";

import AnswerBadge from "./AnswerBadge";

const CorrectAnswer = ({
  questionID,
  number,
  currentAnswer,
  finished,
  children,
}) => {
  const { onAnswerChange } = useContext(AnswersContext);
  const pressed = currentAnswer === number;
  const onClick = () => onAnswerChange(questionID, "");
  return (
    <>
      {!finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-success active"
          aira-pressed="true"
          onClick={onClick}
        >
          {children}
        </button>
      )}
      {!finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-success"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
      {finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-success active disabled"
          aira-pressed="true"
          disabled
        >
          {children}
        </button>
      )}
      {finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-success disabled"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
    </>
  );
};

const IncorrectAnswer = ({
  questionID,
  number,
  currentAnswer,
  finished,
  children,
}) => {
  const { onAnswerChange } = useContext(AnswersContext);
  const pressed = currentAnswer === number;
  const onClick = () => onAnswerChange(questionID, "");
  return (
    <>
      {!finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-danger active"
          aira-pressed="true"
          onClick={onClick}
        >
          {children}
        </button>
      )}
      {!finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-danger"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
      {finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-danger active disabled"
          aira-pressed="true"
          disabled
        >
          {children}
        </button>
      )}
      {finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-danger disabled"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
    </>
  );
};

const DisabledAnswer = ({
  questionID,
  number,
  currentAnswer,
  finished,
  children,
}) => {
  const pressed = currentAnswer === number;
  return (
    <>
      {pressed && (
        <button
          className="list-group-item list-group-item-action active active disabled"
          aira-pressed="true"
          disabled
        >
          {children}
        </button>
      )}
      {!pressed && (
        <button
          className="list-group-item list-group-item-action disabled"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
    </>
  );
};

const AnsweredAnswer = ({
  questionID,
  number,
  currentAnswer,
  finished,
  children,
}) => {
  const { onAnswerChange } = useContext(AnswersContext);
  const pressed = currentAnswer === number;
  const onClick = () => onAnswerChange(questionID, "");
  return (
    <>
      {!finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-primary active"
          aira-pressed="true"
          onClick={onClick}
        >
          {children}
        </button>
      )}
      {!finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-primary"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
      {finished && pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-primary active disabled"
          aira-pressed="true"
          disabled
        >
          {children}
        </button>
      )}
      {finished && !pressed && (
        <button
          className="list-group-item list-group-item-action list-group-item-primary disabled"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
    </>
  );
};

const UnansweredAnswer = ({
  questionID,
  number,
  currentAnswer,
  finished,
  children,
}) => {
  const { onAnswerChange } = useContext(AnswersContext);
  const pressed = currentAnswer === number;
  const onClick = () => onAnswerChange(questionID, number);
  return (
    <>
      {!finished && pressed && (
        <button
          className="list-group-item list-group-item-action active"
          aira-pressed="true"
          onClick={onClick}
        >
          {children}
        </button>
      )}
      {!finished && !pressed && (
        <button
          className="list-group-item list-group-item-action"
          aira-pressed="false"
          onClick={onClick}
        >
          {children}
        </button>
      )}
      {finished && pressed && (
        <button
          className="list-group-item list-group-item-action active disabled"
          aira-pressed="true"
          disabled
        >
          {children}
        </button>
      )}
      {finished && !pressed && (
        <button
          className="list-group-item list-group-item-action disabled"
          aira-pressed="false"
          disabled
        >
          {children}
        </button>
      )}
    </>
  );
};

const Answer = ({
  questionID,
  number,
  showNumbers,
  showAnswers,
  text,
  finished,
}) => {
  const { getAnswer } = useContext(AnswersContext);

  const { question } = useQuestion({ questionID });

  const answerText = (() => {
    const numberString = number && showNumbers ? `${number}.` : "";
    return (
      <span>
        {numberString} {text}
      </span>
    );
  })();

  const correctAnswer = question.answer;

  const currentAnswer = getAnswer(questionID);

  const showCorrectAnswer =
    showAnswers && currentAnswer && number === correctAnswer;

  const showIncorrectAnswer =
    showAnswers &&
    currentAnswer &&
    currentAnswer === number &&
    number !== correctAnswer;

  const showDisabledAnswer = showAnswers
    ? currentAnswer && currentAnswer !== number && number !== correctAnswer
    : currentAnswer && currentAnswer !== number;

  const showAnsweredAnswer =
    !showAnswers && currentAnswer && currentAnswer === number;

  const showUnansweredAnswer = !currentAnswer;

  return (
    <>
      {showCorrectAnswer && (
        <CorrectAnswer
          questionID={questionID}
          number={number}
          currentAnswer={currentAnswer}
          answerText={answerText}
          showAnswers={showAnswers}
          finished={finished}
        >
          <AnswerBadge
            questionID={questionID}
            answerID={number}
            showAnswers={showAnswers}
          />
          {answerText}
        </CorrectAnswer>
      )}
      {showIncorrectAnswer && (
        <IncorrectAnswer
          questionID={questionID}
          number={number}
          currentAnswer={currentAnswer}
          answerText={answerText}
          showAnswers={showAnswers}
          finished={finished}
        >
          <AnswerBadge
            questionID={questionID}
            answerID={number}
            showAnswers={showAnswers}
          />
          {answerText}
        </IncorrectAnswer>
      )}
      {showDisabledAnswer && (
        <DisabledAnswer
          questionID={questionID}
          number={number}
          currentAnswer={currentAnswer}
          answerText={answerText}
          showAnswers={showAnswers}
          finished={finished}
        >
          <AnswerBadge
            questionID={questionID}
            answerID={number}
            showAnswers={showAnswers}
          />
          {answerText}
        </DisabledAnswer>
      )}

      {showUnansweredAnswer && (
        <UnansweredAnswer
          questionID={questionID}
          number={number}
          currentAnswer={currentAnswer}
          answerText={answerText}
          showAnswers={showAnswers}
          finished={finished}
        >
          <AnswerBadge
            questionID={questionID}
            answerID={number}
            showAnswers={showAnswers}
          />
          {answerText}
        </UnansweredAnswer>
      )}
      {showAnsweredAnswer && (
        <AnsweredAnswer
          questionID={questionID}
          number={number}
          currentAnswer={currentAnswer}
          answerText={answerText}
          showAnswers={showAnswers}
          finished={finished}
        >
          <AnswerBadge
            questionID={questionID}
            answerID={number}
            showAnswers={showAnswers}
          />
          {answerText}
        </AnsweredAnswer>
      )}
    </>
  );
};

export default Answer;
